// Libs
import type React from "react";
import { useMemo } from "react";

// Utils
import { useTranslation } from "src/design-system/core/TranslationContext";
import { useIntercom } from "src/hooks/useIntercom";
import { POS_DETAILS_TK, POS_TRANSLATION_NAMESPACE } from "src/views/pos/config";
import { PosParameterTabs, getPosDetailTabRoute } from "src/views/pos/routes";

// Components
import { BiAbacus, BiExpand, BiInfoCircle } from "react-icons/bi";
import { RiStore2Line, RiTruckLine } from "react-icons/ri";
import { SideMenuAWithNavBarLayout } from "src/layouts/SideMenuWithNavBarLayout/SideMenuWithNavBarLayout";
import type { NavBarSection } from "../SharedLayout/NavBar";

interface ParameterLayoutProps {
    children?: React.ReactNode;
}

const TAB_ICON_MAP: Record<PosParameterTabs, React.ElementType> = {
    [PosParameterTabs.Details]: RiStore2Line,
    [PosParameterTabs.ChannelMapping]: BiExpand,
    [PosParameterTabs.ChannelInfo]: BiInfoCircle,
    [PosParameterTabs.PlatformConnection]: RiTruckLine,
    [PosParameterTabs.OtherSettings]: BiAbacus,
};

const TAB_ORDER = [
    PosParameterTabs.Details,
    PosParameterTabs.ChannelMapping,
    PosParameterTabs.ChannelInfo,
    PosParameterTabs.PlatformConnection,
    PosParameterTabs.OtherSettings,
];

function ParameterLayout({ children }: ParameterLayoutProps): JSX.Element {
    useIntercom();

    const { t } = useTranslation(`${POS_TRANSLATION_NAMESPACE}:${POS_DETAILS_TK}`);

    const section: NavBarSection = useMemo(
        () => ({
            items: TAB_ORDER.map((tab) => ({
                href: getPosDetailTabRoute(tab),
                icon: TAB_ICON_MAP[tab],
                title: t(`tabs.${tab}.tab`),
            })),
        }),
        [t]
    );

    return <SideMenuAWithNavBarLayout sections={[section]}>{children}</SideMenuAWithNavBarLayout>;
}

export default ParameterLayout;
